import {
  BRAND_COLORS, BRAND_MARK_FACES, BRAND_MARK_HEIGHT, BRAND_MARK_VIEWBOX, BRAND_MARK_WIDTH,
  BRAND_TILE_RADIUS, BRAND_TILE_SCALE, type BrandFace,
} from '../../constants/brand';
import styles from './BrandMark.module.css';

/**
 * `tile` (the logo): the white i in a blue box. `tileInk`: in an ink box (one-colour
 * print). `tileWhite`: the blue i in a white box, for blue or dark backgrounds.
 * The others draw the bare i (the secondary symbol).
 */
export type BrandMarkVariant = 'tile' | 'tileInk' | 'tileWhite' | 'color' | 'ink' | 'white' | 'onBlue';

type Faces = Record<BrandFace, string>;
const COLOR: Faces = { top: BRAND_COLORS.sky, left: BRAND_COLORS.blue, right: BRAND_COLORS.deep };
const ON_BLUE: Faces = { top: '#ffffff', left: '#d6e2ff', right: '#9dbbff' };
const solid = (c: string): Faces => ({ top: c, left: c, right: c });

const LOOK: Record<BrandMarkVariant, { faces: Faces; hairline: boolean; box?: string }> = {
  tile: { faces: ON_BLUE, hairline: false, box: BRAND_COLORS.blue },
  tileInk: { faces: solid('#ffffff'), hairline: true, box: BRAND_COLORS.ink },
  tileWhite: { faces: COLOR, hairline: false, box: '#ffffff' },
  color: { faces: COLOR, hairline: false },
  ink: { faces: solid(BRAND_COLORS.ink), hairline: true },
  white: { faces: solid('#ffffff'), hairline: true },
  onBlue: { faces: ON_BLUE, hairline: false },
};

interface BrandMarkProps {
  /** Height in px (a box is square; the bare i's width follows its proportions). */
  size?: number;
  variant?: BrandMarkVariant;
  /** Set when the name is written beside the mark, so screen readers do not read it twice. */
  decorative?: boolean;
  className?: string;
}

// One-colour marks are drawn with a hairline between faces, so the column
// still reads as a solid in one colour.
const BrandMark = ({ size = 32, variant = 'tile', decorative = false, className }: BrandMarkProps) => {
  const look = LOOK[variant];
  const faces = BRAND_MARK_FACES.map((f, i) => (
    <path key={i} d={look.hairline ? f.dInset : f.d} fill={look.faces[f.face]} />
  ));
  const side = BRAND_MARK_HEIGHT / BRAND_TILE_SCALE;
  const boxed = look.box !== undefined;
  return (
    <svg
      className={className ? `${styles.mark} ${className}` : styles.mark}
      viewBox={boxed ? `0 0 ${side} ${side}` : BRAND_MARK_VIEWBOX}
      width={boxed ? size : Math.round((size * BRAND_MARK_WIDTH) / BRAND_MARK_HEIGHT * 100) / 100}
      height={size}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Infrio'}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
    >
      {boxed ? (
        <>
          <rect width={side} height={side} rx={side * BRAND_TILE_RADIUS} fill={look.box} />
          <g transform={`translate(${(side - BRAND_MARK_WIDTH) / 2} ${(side - BRAND_MARK_HEIGHT) / 2})`}>{faces}</g>
        </>
      ) : faces}
    </svg>
  );
};

export default BrandMark;
